import { useAtom, useAtomValue } from 'jotai';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import React, { memo } from 'react';
import { Link, useLocation } from 'react-router-dom';

import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { sidebarCollapseAtom } from '@/store/atoms';
import type {
  NavItem,
  SidebarProps,
  SidebarSectionProps,
  SidebarLinkProps,
  SidebarToggleProps,
} from '@/types/sidebar';
import { cn } from '@/utils/utils';

// Main Sidebar Container
export const Sidebar = ({ children, className }: SidebarProps) => {
  const isCollapsed = useAtomValue(sidebarCollapseAtom);

  return (
    <TooltipProvider>
      <div
        className={cn(
          'bg-sidebar border-sidebar-border shadow-elegant flex flex-col border-r transition-all duration-300',
          isCollapsed ? 'w-[72px]' : 'w-64',
          className,
        )}
        data-collapsed={isCollapsed}
      >
        {React.Children.map(children, (child) => {
          if (
            React.isValidElement(child) &&
            typeof child.type !== 'string' // Only pass to custom components, not DOM elements
          ) {
            return React.cloneElement(child, { isCollapsed } as {
              isCollapsed: boolean;
            });
          }
          return child;
        })}
      </div>
    </TooltipProvider>
  );
};

// Toggle Button Component
const SidebarToggle = memo(function SidebarToggle({
  isCollapsed,
  onToggle,
}: SidebarToggleProps) {
  return (
    <div className="w-full px-2 py-2">
      <div
        onClick={onToggle}
        data-testid="sidebar-toggle"
        aria-expanded={!isCollapsed}
        aria-label={isCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            onToggle();
          }
        }}
        className={cn(
          'flex h-10 w-full cursor-pointer items-center justify-center rounded-xl',
          'bg-muted/50 hover:bg-muted border-border border',
          'transition-all duration-200 hover:scale-105',
          'focus-visible:ring-ring focus-visible:ring-2 focus-visible:outline-none',
          isCollapsed ? '' : 'hover:bg-muted/50 w-8',
        )}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4" />
        ) : (
          <ChevronLeft className="h-4 w-4" />
        )}
      </div>
    </div>
  );
});

// Section Container
const SidebarSection = ({
  children,
  title,
  className,
}: SidebarSectionProps) => {
  const isCollapsed = useAtomValue(sidebarCollapseAtom);

  return (
    <div className={cn('p-2 py-3', className)}>
      {title && !isCollapsed && (
        <div className="text-muted-foreground px-3 py-2 text-xs font-semibold tracking-wider uppercase">
          {title}
        </div>
      )}
      <div className={cn('space-y-1', !isCollapsed && 'pl-4')}>{children}</div>
    </div>
  );
};

// Individual Navigation Link
const SidebarLink = memo(function SidebarLink({
  item,
  isCollapsed,
}: SidebarLinkProps) {
  const location = useLocation();
  const isActive =
    location.pathname === item.href ||
    location.pathname.startsWith(`${item.href}/`);
  const Icon = item.icon;

  const linkContent = (
    <Link
      to={item.href}
      data-testid="sidebar-link"
      aria-current={isActive ? 'page' : undefined}
      className={cn(
        'flex items-center gap-3 rounded-md px-3 py-1.5 transition-all duration-200',
        'focus-visible:ring-ring hover:bg-gray-200/80 focus-visible:ring-2 focus-visible:outline-none',
        'group text-muted-foreground relative',
        isActive && [
          'bg-sidebar-active border-sidebar-active-border border-primary border-r-2 bg-orange-50',
          'font-medium text-orange-700 shadow-sm hover:bg-orange-50',
        ],
        isCollapsed && 'justify-center px-2',
      )}
    >
      <Icon
        className={cn(
          'h-5 w-5 transition-transform duration-200 group-hover:scale-110',
          isActive && 'text-primary',
        )}
      />
      {!isCollapsed && (
        <span className="text-sm transition-all duration-200 group-hover:translate-x-0.5">
          {item.label}
        </span>
      )}
    </Link>
  );

  if (isCollapsed) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{linkContent}</TooltipTrigger>
        <TooltipContent side="right" className="ml-2">
          <span>{item.label}</span>
        </TooltipContent>
      </Tooltip>
    );
  }

  return linkContent;
});

// Compound Component Exports
const SidebarToggleWrapper = () => {
  const [isCollapsed, setIsCollapsed] = useAtom(sidebarCollapseAtom);
  const toggle = () => {
    setIsCollapsed(!isCollapsed);
  };
  return <SidebarToggle isCollapsed={isCollapsed} onToggle={toggle} />;
};

const SidebarLinkWrapper = ({ item }: { item: NavItem }) => {
  const isCollapsed = useAtomValue(sidebarCollapseAtom);
  return <SidebarLink item={item} isCollapsed={isCollapsed} />;
};

Sidebar.Toggle = SidebarToggleWrapper;
Sidebar.Section = SidebarSection;
Sidebar.Link = SidebarLinkWrapper;
